<template>
  <div>
    <v-container fluid fill-height class="login-container">
      <v-layout align-center justify-center row wrap>
        <!-- Main Login Card -->
        <v-flex xs12 sm8 md6 lg5 xl4>
          <v-card class="login-card elevation-12 pa-3">
            <!-- Header Section -->
            <v-card-title class="login-header">
              <v-layout column align-center>
                <img
                  src="@/assets/back-office/logo.png"
                  alt="WeServe Logo"
                  width="200px"
                />
                <p class="subheading black--text text-xs-center mb-0 mt-3">
                  {{ $t("login.subtitle") }}
                </p>
              </v-layout>
            </v-card-title>

            <!-- Login Form Section -->
            <v-card-text class="login-form-section">
              <!-- Error Alert -->
              <v-alert
                v-if="errorMessage"
                type="error"
                dismissible
                class="mb-4"
                @input="errorMessage = ''"
              >
                {{ errorMessage }}
              </v-alert>

              <!-- Success Message -->
              <v-alert
                v-if="successMessage"
                type="success"
                dismissible
                class="mb-4"
                @input="successMessage = ''"
              >
                {{ successMessage }}
              </v-alert>

              <!-- Login Form -->
              <v-form
                ref="loginForm"
                v-model="formValid"
                @submit.prevent="login"
              >
                <v-text-field
                  v-model="phone_mail"
                  :label="$t('login.emailPhone')"
                  :rules="emailPhoneRules"
                  prepend-inner-icon="person"
                  outline
                  color="primary"
                  autocomplete="username"
                  :disabled="logingIn"
                  @keyup.enter="login"
                  class="mb-3 custom-input"
                ></v-text-field>

                <v-text-field
                  v-model="password"
                  :label="$t('login.password')"
                  :rules="passwordRules"
                  :type="showPassword ? 'text' : 'password'"
                  prepend-inner-icon="lock"
                  :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                  outline
                  color="primary"
                  autocomplete="current-password"
                  :disabled="logingIn"
                  @click:append="showPassword = !showPassword"
                  @keyup.enter="login"
                  class="mb-3 custom-input"
                ></v-text-field>

                <!-- Login Button -->
                <v-btn
                  type="submit"
                  :loading="logingIn"
                  :disabled="!formValid || logingIn"
                  block
                  large
                  color="#fddf00"
                  style="color: black !important"
                  class="login-btn mb-4"
                  depressed
                >
                  <v-icon left style="color: black !important">input</v-icon>
                  {{ $t("login.loginButton") }}
                </v-btn>
              </v-form>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- Side Illustration (Hidden on mobile) -->
        <v-flex xs12 md6 lg7 xl8 class="hidden-sm-and-down">
          <v-layout column align-center justify-center class="side-content">
            <!-- Welcome Section with Backdrop Filter -->
            <div class="welcome-section">
              <h2
                class="display-2 font-weight-light white--text text-xs-center welcome-title"
              >
                {{ $t("login.welcomeBack") }}
              </h2>
              <p class="title white--text text-xs-center welcome-text">
                {{ $t("login.welcomeMessage") }}
              </p>

              <!-- Feature highlights -->
              <v-layout row wrap justify-center class="feature-list">
                <v-flex xs12 class="feature-item">
                  <v-icon color="white" class="feature-icon">dashboard</v-icon>
                  <span class="white--text feature-text"
                    >Dashboard Management</span
                  >
                </v-flex>
                <v-flex xs12 class="feature-item">
                  <v-icon color="white" class="feature-icon">restaurant</v-icon>
                  <span class="white--text feature-text">Order Management</span>
                </v-flex>
                <v-flex xs12 class="feature-item">
                  <v-icon color="white" class="feature-icon">people</v-icon>
                  <span class="white--text feature-text"
                    >Customer Management</span
                  >
                </v-flex>
                <v-flex xs12 class="feature-item">
                  <v-icon color="white" class="feature-icon">assessment</v-icon>
                  <span class="white--text feature-text"
                    >Analytics & Reports</span
                  >
                </v-flex>
              </v-layout>
            </div>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>
    <!-- Footer -->
    <v-footer app class="custom-footer">
      <v-layout row align-center>
        <v-flex xs12 sm6>
          <span class="caption"
            >WESERVE BACKOFFICE v{{ pjson.version || "" }}</span
          >
        </v-flex>
        <v-flex xs12 sm6 class="text-xs-right">
          <span class="caption">&copy; 2019-2025 Powered by WESERVE</span>
        </v-flex>
      </v-layout>
    </v-footer>
  </div>
</template>

<script>
import { mapState } from "vuex";
import pjson from "./../../../../package.json";

export default {
  name: "BackOfficeLogin",
  data() {
    return {
      pjson,
      drawer: null,
      phone_mail: "",
      password: "",
      showPassword: false,
      formValid: false,
      logingIn: false,
      errorMessage: "",
      successMessage: "",
      // Validation rules
      emailPhoneRules: [
        (v) => !!v || this.$t("validation.required"),
        (v) => {
          // Check if it's an email or phone number
          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const phonePattern = /^[0-9]{9,15}$/;
          return (
            emailPattern.test(v) ||
            phonePattern.test(v) ||
            this.$t("validation.emailPhoneInvalid")
          );
        },
      ],
      passwordRules: [
        (v) => !!v || this.$t("validation.required"),
        (v) => (v && v.length >= 6) || this.$t("validation.passwordMinLength"),
      ],
    };
  },
  computed: {
    ...mapState({
      role: (state) => state.role,
    }),
  },
  props: {
    source: String,
  },
  mounted() {
    // Add welcome animation
    this.showWelcomeAnimation();
  },
  methods: {
    async login() {
      // Validate form first
      if (!this.$refs.loginForm.validate()) {
        return;
      }

      this.logingIn = true;
      this.errorMessage = "";

      try {
        const result = await this.$store.dispatch("login", {
          user_login: this.phone_mail,
          user_password: this.password,
        });

        if (result === true) {
          // Show success message briefly
          this.successMessage = this.$t("login.loginSuccess");

          // Success - navigation will be handled by the store action
          this.$emit("login-success");
          return;
        } else {
          // Handle login failure
          this.errorMessage =
            result.message || this.$t("login.error.invalidCredentials");
        }
      } catch (error) {
        console.error("Login error:", error);
        this.errorMessage =
          error.response.data.message || this.$t("login.error.networkError");
      }

      this.logingIn = false;
    },

    showWelcomeAnimation() {
      // Add subtle animation on page load
      setTimeout(() => {
        const card = document.querySelector(".login-card");
        if (card) {
          card.classList.add("animate-in");
        }
      }, 100);
    },
  },
};
</script>

<style scoped>
/* WeServe CI Color Variables */
:root {
  --weserve-primary: #fddf00;
  --weserve-primary-dark: #e6c800;
  --weserve-primary-light: #fff3b3;
  --weserve-dark: #2c3e50;
  --weserve-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

/* Main Container */
.login-container {
  min-height: 100vh;
  background: var(--weserve-gradient);
  position: relative;
  overflow: hidden;
}

.login-container::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
      circle at 20% 80%,
      rgba(253, 223, 0, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 20%,
      rgba(253, 223, 0, 0.15) 0%,
      transparent 50%
    ),
    url("../../../assets/Pos/bg-login4.jpg") center/cover;
  opacity: 0.8;
  z-index: 0;
}

/* Login Card */
.login-card {
  position: relative;
  z-index: 1;
  border-radius: 20px !important;
  overflow: hidden;
  backdrop-filter: blur(20px);
  background: rgba(255, 255, 255, 0.95) !important;
  max-width: 450px;
  margin: 0 auto;
  transform: translateY(20px);
  opacity: 0;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.login-card.animate-in {
  transform: translateY(0);
  opacity: 1;
}

/* Header Section */
.login-header {
  background: linear-gradient(
    135deg,
    var(--weserve-primary) 0%,
    var(--weserve-primary-dark) 100%
  );
  border-radius: 20px 20px 0 0;
}

.logo-img {
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
  transition: transform 0.3s ease;
}

.logo-img:hover {
  transform: scale(1.05);
}

/* Form Section */
.login-form-section {
  padding: 20px 30px !important;
  background: rgba(255, 255, 255, 0.98);
}

/* Custom Input Styling */
.custom-input .input-group--text-field.input-group--outline {
  border-radius: 12px;
}

.custom-input
  .input-group--text-field.input-group--outline
  .input-group__input {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  transition: all 0.3s ease;
}

.custom-input
  .input-group--text-field.input-group--outline:hover
  .input-group__input {
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 4px 12px rgba(253, 223, 0, 0.2);
}

.custom-input
  .input-group--text-field.input-group--outline.input-group--focused
  .input-group__input {
  border-color: var(--weserve-primary) !important;
  border-width: 2px !important;
  box-shadow: 0 0 0 3px rgba(253, 223, 0, 0.1);
}

.custom-input .input-group__prepend-icon .icon {
  color: var(--weserve-primary) !important;
}

/* Login Button */
.login-btn {
  color: var(--weserve-dark) !important;
  font-weight: bold !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
  border-radius: 12px !important;
  height: 56px !important;
  box-shadow: 0 4px 15px rgba(253, 223, 0, 0.4) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.login-btn:hover {
  background: linear-gradient(
    135deg,
    var(--weserve-primary-dark) 0%,
    #d4b800 100%
  ) !important;
  box-shadow: 0 6px 20px rgba(253, 223, 0, 0.6) !important;
  transform: translateY(-2px);
}

.login-btn:active {
  transform: translateY(0);
}

/* Checkbox Styling */
.input-group--selection-controls.primary--text .input-group__input {
  color: var(--weserve-primary) !important;
}

/* Side Content */
.side-content {
  padding: 60px 40px;
  position: relative;
  z-index: 1;
}

.side-image {
  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.1));
  opacity: 0.9;
}

/* Welcome Section with Backdrop Filter */
.welcome-section {
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 24px;
  padding: 3rem 2.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  max-width: 700px;
  min-height: 400px;
  transition: all 0.3s ease;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.welcome-section:hover {
  background: rgba(0, 0, 0, 0.5);
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
}

.feature-list {
  max-width: 500px;
}

.feature-item {
  display: flex;
  align-items: center;
  font-size: 16px;
  padding: 15px;
}

.feature-list .icon {
  margin-right: 8px;
  opacity: 0.9;
}

/* Footer */
.custom-footer {
  background: rgba(0, 0, 0, 0.4) !important;
  color: white !important;
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 10px 20px;
  height: auto !important;
}
/* Alert Styling */
.alert {
  border-radius: 12px !important;
  border: none !important;
}

.alert--success {
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%) !important;
}

.alert--error {
  background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%) !important;
}

/* Theme Overrides for Vuetify 1.x */
.theme--light.btn.primary {
  background: linear-gradient(
    135deg,
    var(--weserve-primary) 0%,
    var(--weserve-primary-dark) 100%
  ) !important;
  color: var(--weserve-dark) !important;
}

.theme--light.btn.primary:hover {
  background: linear-gradient(
    135deg,
    var(--weserve-primary-dark) 0%,
    #d4b800 100%
  ) !important;
}

/* Responsive Design */
@media (max-width: 960px) {
  .login-card {
    margin: 16px;
    border-radius: 16px !important;
  }

  .login-header {
    padding: 30px 20px 20px !important;
  }

  .login-form-section {
    padding: 30px 20px !important;
  }

  .side-content {
    padding: 40px 20px;
    height: auto;
    min-height: 50vh;
  }

  .welcome-section {
    padding: 40px 25px;
    border-radius: 20px;
    min-height: 300px;
  }
}

@media (max-width: 600px) {
  .login-card {
    margin: 8px;
    border-radius: 12px !important;
  }

  .custom-footer .layout {
    flex-direction: column;
    text-align: center;
  }

  .custom-footer .flex {
    margin-bottom: 8px;
  }

  .welcome-section {
    padding: 2rem 1.5rem;
    border-radius: 16px;
    min-height: 250px;
  }

  .feature-item {
    padding: 0.5rem 0.75rem;
    margin-bottom: 0.75rem;
  }

  .welcome-title {
    font-size: 2rem !important;
    margin-bottom: 1rem !important;
  }

  .welcome-text {
    margin-bottom: 1.5rem !important;
  }
}

/* Loading Animation */
.btn--loading .btn__content {
  opacity: 0;
}

/* Focus States for Accessibility */
.btn:focus {
  box-shadow: 0 0 0 3px rgba(253, 223, 0, 0.3) !important;
}

.input-group--text-field input:focus {
  outline: none;
}

/* Smooth Transitions */
* {
  transition: color 0.3s ease, background-color 0.3s ease,
    border-color 0.3s ease;
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}

::-webkit-scrollbar-thumb {
  background: var(--weserve-primary);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--weserve-primary-dark);
}

/* Typography Enhancements */
.display-1 {
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.display-2 {
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* Welcome Section Text Effects */
.welcome-title {
  font-size: 2.5rem !important;
  line-height: 1.2;
  margin-bottom: 1.5rem !important;
}

.welcome-text {
  line-height: 2.5rem !important;
  opacity: 0.95;
  margin-bottom: 2rem !important;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

.feature-text {
  font-weight: 500;
}

.feature-icon {
  margin-right: 12px !important;
}

/* Animation for form elements */
.custom-input {
  transition: transform 0.2s ease;
}

.custom-input:hover {
  transform: translateY(-1px);
}

/* Icon animations */
.icon {
  transition: transform 0.2s ease;
}

.btn:hover .icon {
  transform: scale(1.1);
}

/* Divider styling */
.divider {
  background: linear-gradient(
    90deg,
    transparent,
    rgba(0, 0, 0, 0.12),
    transparent
  );
}
</style>